import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Bot, Save, Upload, History, RotateCcw, Check, Loader2,
  Trash2, Eye, FileCode2, KeyRound, Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

function PanelModulo({ modulo, providers, onReload }) {
  const [cfg, setCfg] = useState(modulo);
  const [prompt, setPrompt] = useState('');
  const [versiones, setVersiones] = useState([]);
  const [titulo, setTitulo] = useState('');
  const [notas, setNotas] = useState('');
  const [busy, setBusy] = useState('');
  const [verVersion, setVerVersion] = useState(null);
  const [apiKey, setApiKey] = useState('');

  const cargar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/prompts?modulo=${modulo.modulo}`, tokenHeader());
      setVersiones(r.data || []);
      const activa = (r.data || []).find((v) => v.activo);
      if (activa?.contenido_md) {
        setPrompt(activa.contenido_md);
      } else {
        const rf = await axios.get(`${API}/api/hipotecas/prompts/fabrica/${modulo.modulo}`, tokenHeader());
        setPrompt(rf.data.contenido_md || '');
      }
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar el prompt', variant: 'destructive' });
    }
  }, [modulo.modulo]);

  useEffect(() => { setCfg(modulo); }, [modulo]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardarModelo = async () => {
    setBusy('modelo');
    try {
      const payload = {
        provider: cfg.provider,
        model: cfg.model,
        max_tokens: Number(cfg.max_tokens),
        temperature: Number(cfg.temperature),
        base_url_propia: cfg.base_url_propia || '',
      };
      if (apiKey !== '') payload.api_key_propia = apiKey;
      const r = await axios.put(`${API}/api/hipotecas/modulos/${modulo.modulo}`, payload, tokenHeader());
      setCfg(r.data);
      setApiKey('');
      toast({ title: 'Configuración del modelo guardada' });
      onReload?.();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  const guardarPrompt = async () => {
    if ((prompt || '').trim().length < 200) {
      toast({ title: 'Prompt demasiado corto', variant: 'destructive' });
      return;
    }
    setBusy('prompt');
    try {
      await axios.post(`${API}/api/hipotecas/prompts`, {
        modulo: modulo.modulo, contenido_md: prompt, titulo: titulo || null, notas: notas || null,
      }, tokenHeader());
      setTitulo(''); setNotas('');
      toast({ title: 'Nueva versión activada', description: 'La versión anterior queda en el historial.' });
      await cargar();
      onReload?.();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  const subirMd = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('modulo', modulo.modulo);
    fd.append('titulo', file.name);
    setBusy('upload');
    try {
      await axios.post(`${API}/api/hipotecas/prompts/upload`, fd, {
        ...tokenHeader(),
        headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Prompt subido y activado', description: file.name });
      await cargar();
      onReload?.();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusy('');
      e.target.value = '';
    }
  };

  const activar = async (v) => {
    setBusy(`activar-${v.id}`);
    try {
      const r = await axios.post(`${API}/api/hipotecas/prompts/${v.id}/activar`, {}, tokenHeader());
      setPrompt(r.data.contenido_md || '');
      toast({ title: `Versión ${v.version} activada` });
      await cargar();
      onReload?.();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  const borrar = async (v) => {
    if (!window.confirm(`¿Borrar la versión ${v.version}?`)) return;
    try {
      await axios.delete(`${API}/api/hipotecas/prompts/${v.id}`, tokenHeader());
      toast({ title: 'Versión eliminada' });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const ver = async (v) => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/prompts/${v.id}`, tokenHeader());
      setVerVersion(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const cargarFabrica = async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/prompts/fabrica/${modulo.modulo}`, tokenHeader());
      setPrompt(r.data.contenido_md || '');
      toast({ title: 'Prompt de fábrica cargado en el editor', description: 'Guárdalo como nueva versión para activarlo.' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const modelos = providers?.modelos || {};

  return (
    <div className="space-y-6" data-testid={`panel-${modulo.modulo}`}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" /> Modelo de IA
          </CardTitle>
          <CardDescription>
            Por defecto se usa la clave universal de Emergent. Si prefieres tu propia cuenta, introduce tu API key
            y se usará esa para este módulo. Para Claude/OpenAI/Gemini también puedes configurar tus claves de forma
            global en Configuración → Asistente IA (independencia de proveedor) o dar de alta proveedores externos.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Proveedor</Label>
            <Select value={cfg.provider} onValueChange={(v) => setCfg({ ...cfg, provider: v, model: modelos[v]?.[0] || '' })}>
              <SelectTrigger data-testid={`select-provider-${modulo.modulo}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(modelos).map((p) => (
                  <SelectItem key={p} value={p}>
                    {providers?.display_names?.[p]
                      || (p === 'anthropic' ? 'Claude (Anthropic)' : p === 'openai' ? 'OpenAI' : p === 'gemini' ? 'Gemini (Google)' : p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Modelo</Label>
            <Select value={cfg.model} onValueChange={(v) => setCfg({ ...cfg, model: v })}>
              <SelectTrigger data-testid={`select-model-${modulo.modulo}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {(modelos[cfg.provider] || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Máximo de tokens de salida</Label>
            <Input
              type="number" value={cfg.max_tokens}
              onChange={(e) => setCfg({ ...cfg, max_tokens: e.target.value })}
              data-testid={`input-maxtokens-${modulo.modulo}`}
            />
          </div>
          <div>
            <Label>Temperatura (0 = determinista)</Label>
            <Input
              type="number" step="0.1" min="0" max="1" value={cfg.temperature}
              onChange={(e) => setCfg({ ...cfg, temperature: e.target.value })}
              data-testid={`input-temp-${modulo.modulo}`}
            />
          </div>
          <div>
            <Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> API key propia (opcional)</Label>
            <Input
              type="password"
              placeholder={cfg.tiene_api_key_propia ? '•••••••• (guardada) — escribe para cambiarla' : 'Dejar vacío = clave universal Emergent'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid={`input-apikey-${modulo.modulo}`}
            />
          </div>
          <div>
            <Label>Base URL propia (solo proveedores compatibles OpenAI)</Label>
            <Input
              value={cfg.base_url_propia || ''}
              onChange={(e) => setCfg({ ...cfg, base_url_propia: e.target.value })}
              placeholder="https://api.deepseek.com/v1"
              data-testid={`input-baseurl-${modulo.modulo}`}
            />
          </div>
          <div className="md:col-span-2">
            <Button onClick={guardarModelo} disabled={busy === 'modelo'} data-testid={`btn-guardar-modelo-${modulo.modulo}`}>
              {busy === 'modelo' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar modelo
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileCode2 className="w-4 h-4 text-indigo-600" /> Prompt que se envía a la IA
              </CardTitle>
              <CardDescription>
                Documento Markdown completo. Al guardar se crea una versión nueva y la anterior queda recuperable.
                {modulo.prompt_es_de_fabrica
                  ? ' Ahora mismo está activo el prompt de fábrica.'
                  : ` Versión activa: ${modulo.prompt_version_activa}.`}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={cargarFabrica} data-testid={`btn-fabrica-${modulo.modulo}`}>
                <RotateCcw className="w-4 h-4 mr-2" /> Cargar prompt de fábrica
              </Button>
              <label className="inline-flex">
                <Button asChild variant="outline" size="sm" data-testid={`btn-upload-md-${modulo.modulo}`}>
                  <span className="cursor-pointer">
                    {busy === 'upload' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Subir .md
                  </span>
                </Button>
                <input type="file" hidden accept=".md,.txt,.markdown" onChange={subirMd} data-testid={`input-md-${modulo.modulo}`} />
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="font-mono text-xs h-[420px]"
            spellCheck={false}
            data-testid={`textarea-prompt-${modulo.modulo}`}
          />
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Título de la versión</Label>
              <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="p.ej. v1.2 — añadida STS abril 2026" data-testid={`input-titulo-${modulo.modulo}`} />
            </div>
            <div>
              <Label>Notas del cambio</Label>
              <Input value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Qué has cambiado y por qué" data-testid={`input-notas-${modulo.modulo}`} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={guardarPrompt} disabled={busy === 'prompt'} data-testid={`btn-guardar-prompt-${modulo.modulo}`}>
              {busy === 'prompt' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar como nueva versión y activar
            </Button>
            <span className="text-xs text-gray-500">{(prompt || '').length.toLocaleString('es-ES')} caracteres</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-indigo-600" /> Historial de versiones
          </CardTitle>
          <CardDescription>Puedes recuperar cualquier versión anterior y dejarla activa.</CardDescription>
        </CardHeader>
        <CardContent>
          {versiones.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Sin versiones guardadas: se está usando el prompt de fábrica.</p>
          ) : (
            <div className="space-y-2" data-testid={`historial-${modulo.modulo}`}>
              {versiones.map((v) => (
                <div key={v.id} className="border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`version-${v.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">v{v.version} — {v.titulo}</span>
                      {v.activo && <Badge className="bg-green-600 text-white"><Check className="w-3 h-3 mr-1" />Activa</Badge>}
                      <Badge variant="outline">{v.origen}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(v.created_at).toLocaleString('es-ES')} · {v.creado_por_nombre || '—'} · {v.longitud.toLocaleString('es-ES')} caracteres
                    </p>
                    {v.notas && <p className="text-xs text-gray-600 mt-1">{v.notas}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => ver(v)} data-testid={`btn-ver-version-${v.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {!v.activo && (
                      <>
                        <Button size="sm" onClick={() => activar(v)} disabled={busy === `activar-${v.id}`} data-testid={`btn-activar-${v.id}`}>
                          {busy === `activar-${v.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                          Activar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => borrar(v)} data-testid={`btn-borrar-version-${v.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!verVersion} onOpenChange={() => setVerVersion(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Versión {verVersion?.version} — {verVersion?.titulo}</DialogTitle>
          </DialogHeader>
          <pre className="text-xs whitespace-pre-wrap max-h-[60vh] overflow-auto bg-gray-50 p-3 rounded">
            {verVersion?.contenido_md}
          </pre>
          <Button variant="outline" onClick={() => { setPrompt(verVersion.contenido_md); setVerVersion(null); toast({ title: 'Contenido cargado en el editor' }); }}>
            Cargar en el editor
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ConfiguracionPromptsIA() {
  const navigate = useNavigate();
  const [modulos, setModulos] = useState([]);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const [rm, rp] = await Promise.all([
        axios.get(`${API}/api/hipotecas/modulos`, tokenHeader()),
        axios.get(`${API}/api/hipotecas/providers`, tokenHeader()),
      ]);
      setModulos(rm.data || []);
      setProviders(rp.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los módulos de IA', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="config-prompts-ia-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/configuracion')} data-testid="btn-volver-config">
          <ArrowLeft className="w-4 h-4 mr-1" /> Configuración
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-indigo-600" />
            Prompts y modelos de Análisis IA
          </h1>
          <p className="text-gray-600 mt-1">
            Edita el prompt que se envía a la IA en cada módulo de análisis y elige el modelo. Todas las versiones quedan guardadas.
          </p>
        </div>
      </div>

      <Tabs defaultValue={modulos[0]?.modulo}>
        <TabsList>
          {modulos.map((m) => (
            <TabsTrigger key={m.modulo} value={m.modulo} data-testid={`tab-${m.modulo}`}>{m.nombre}</TabsTrigger>
          ))}
        </TabsList>
        {modulos.map((m) => (
          <TabsContent key={m.modulo} value={m.modulo} className="mt-4">
            <PanelModulo modulo={m} providers={providers} onReload={cargar} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
